import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { useAdminDomainDetail, useToggleDomainArchive } from '@/hooks/useAdminDomains';
import { ArrowLeft, Building2, Calendar, Globe, Archive, ArchiveRestore } from 'lucide-react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { DomainFoldersCard } from '../components/DomainFoldersCard';
import { DomainSyncStatus } from '../components/DomainSyncStatus';
import { ForceSyncButton } from '../components/ForceSyncButton';
import { formatDateTime } from '@/utils/formatters';

export function DomainDetailPage() {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { data: response, isLoading } = useAdminDomainDetail(uuid);
  const domain = (response as any)?.data ?? response;
  const toggleArchive = useToggleDomainArchive();

  if (isLoading) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-64" /></div>;
  if (!domain || !domain.uuid) return <div className="py-12 text-center text-neutral-600 dark:text-neutral-400">Domaine introuvable</div>;

  const orgName = domain.organisation?.name || domain.organisationName || '';
  const orgUuid = domain.organisation?.uuid || domain.organisationUuid;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" icon={<ArrowLeft className="h-4 w-4" />} onClick={() => navigate('/domains')} />
          <h1 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{domain.name}</h1>
          {domain.isArchived && <Badge variant="danger">Archivé</Badge>}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={domain.isArchived ? 'outline' : 'danger'}
            size="sm"
            icon={domain.isArchived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
            onClick={() => toggleArchive.mutate(domain.uuid)}
            loading={toggleArchive.isPending}
          >
            {domain.isArchived ? 'Désarchiver' : 'Archiver'}
          </Button>
          <ForceSyncButton domainUuid={domain.uuid} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader title="Informations" />
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                <Globe className="h-4 w-4 text-neutral-400 dark:text-neutral-600" />
                <span>{domain.name}</span>
              </div>
              {orgName && (
                <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                  <Building2 className="h-4 w-4 text-neutral-400 dark:text-neutral-600" />
                  {orgUuid ? (
                    <Link to={`/organisations/${orgUuid}`} className="hover:text-primary-500 underline">{orgName}</Link>
                  ) : (
                    <span>{orgName}</span>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                <Calendar className="h-4 w-4 text-neutral-400 dark:text-neutral-600" />
                <span>Créé le {formatDateTime(domain.createdAt)}</span>
              </div>
              {domain.isArchived && domain.archivedAt && (
                <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                  <Archive className="h-4 w-4" />
                  <span>Archivé le {formatDateTime(domain.archivedAt)}</span>
                </div>
              )}
              <div>
                <p className="mb-1 text-xs text-neutral-500">Synchronisation</p>
                <DomainSyncStatus status={domain.lastSyncStatus} lastSyncAt={domain.lastSyncAt} />
              </div>
            </div>
          </CardContent>
        </Card>
        <div className="lg:col-span-2">
          <DomainFoldersCard domain={domain} />
        </div>
      </div>
    </div>
  );
}
